import { useEffect, useContext, useRef } from 'react';

// routes
import Router from './routes';
// theme
import ThemeProvider from './theme';
// redux
import { useDispatch } from './redux/store';
import useAuth from './hooks/useAuth';

// components
import RtlLayout from './components/RtlLayout';
import { ChartStyle } from './components/chart';
import ScrollToTop from './components/ScrollToTop';
import { ProgressBarStyle } from './components/ProgressBar';
import NotistackProvider from './components/NotistackProvider';
import ThemeColorPresets from './components/ThemeColorPresets';
import ThemeLocalization from './components/ThemeLocalization';
import MotionLazyContainer from './components/animate/MotionLazyContainer';

// utils
import { OnConnectCompany } from './utils/socketControl';
import { clearLastEventIdForTopic, setLastEventIdForTopic } from './utils/socketReplayCursor';
import { SocketContext } from './contexts/SocketIo';

import { updateContactRealTime, deleteContactRealTime, getConversations } from './redux/slices/chat'; // reducers novos

// ----------------------------------------------------------------------

export default function App() {
  const { user, update } = useAuth();
  const dispatch = useDispatch();
  const socket = useContext(SocketContext);
  const handlerRef = useRef(null);
  const channelRef = useRef(null);
  const processedEventIdsRef = useRef(new Set());
  const processedEventOrderRef = useRef([]);

  useEffect(() => {
    // Read socket msg in all application
    if (!user || !socket) return undefined;

    if (channelRef.current !== user.companyId) {
      processedEventIdsRef.current.clear();
      processedEventOrderRef.current = [];
    }

    // Limpa handler anterior se o canal mudar ou o user mudar
    if (handlerRef.current && channelRef.current) {
      socket.off(channelRef.current, handlerRef.current);
    }

    const refreshConversations = () => {
      if (
        user?.acl?.profile === 'agent' ||
        user?.acl?.profile === 'admin' ||
        user?.acl?.profile === 'manager' ||
        user?.acl?.profile === 'supervisor'
      ) {
        dispatch(getConversations(user));
      }
    };

    const rememberEventId = (eventId) => {
      const seenIds = processedEventIdsRef.current;
      const seenOrder = processedEventOrderRef.current;

      if (seenIds.has(eventId)) {
        return false;
      }

      seenIds.add(eventId);
      seenOrder.push(eventId);

      if (seenOrder.length > 5000) {
        const oldestId = seenOrder.shift();
        if (oldestId) {
          seenIds.delete(oldestId);
        }
      }

      setLastEventIdForTopic(user.companyId, eventId);
      return true;
    };

    const handler = (msg) => {
      const eventId = msg?.metadata?.eventId;
      if (typeof eventId === 'string' && eventId.length > 0 && !rememberEventId(eventId)) {
        return;
      }

      const messageType = msg?.metadata?.type || msg?.type;

      if (messageType === 'contactUpdate') {
        dispatch(updateContactRealTime(msg.data));
      }

      if (messageType === 'contactDelete') {
        dispatch(deleteContactRealTime(msg.data));
      }

      OnConnectCompany(msg, user, dispatch, update);
    };

    const onResyncRequired = (payload) => {
      if (!payload || payload.topic !== user.companyId) return;

      console.warn('[App.js] Socket replay buffer gap detected. Re-syncing from API.', payload);
      clearLastEventIdForTopic(user.companyId);
      processedEventIdsRef.current.clear();
      processedEventOrderRef.current = [];
      refreshConversations();
    };

    handlerRef.current = handler;
    channelRef.current = user.companyId;

    socket.on(user.companyId, handler);
    socket.on('realtime:resync-required', onResyncRequired);

    return () => {
      socket.off(user.companyId, handler);
      socket.off('realtime:resync-required', onResyncRequired);
    };
  }, [dispatch, user, socket, update]);

  return (
    <ThemeProvider>
      <ThemeColorPresets>
        <ThemeLocalization>
          <RtlLayout>
            <NotistackProvider>
              <MotionLazyContainer>
                <ProgressBarStyle />
                <ChartStyle />
                <ScrollToTop />
                <Router />
              </MotionLazyContainer>
            </NotistackProvider>
          </RtlLayout>
        </ThemeLocalization>
      </ThemeColorPresets>
    </ThemeProvider>
  );
}
